import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Check, X, Edit3, ExternalLink, Sparkles,
  UserRound, Send, MessageCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }); }
  catch { return iso; }
};

const TRIGGER_LABEL = {
  salesman_message: 'Salesman posted',
  mention: '@Ganapathi tagged',
  manual: 'Manual analyze',
  auto: 'Auto',
};

export default function GanapathiReview() {
  const navigate = useNavigate();
  const [state, setState]     = useState(null);
  const [drafts, setDrafts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId]   = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText]   = useState('');
  const [salesmanReplyId, setSalesmanReplyId] = useState(null);
  const [salesmanReplyText, setSalesmanReplyText] = useState('');
  const [tab, setTab] = useState('pending');

  const load = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get('/ganapathi/state'),
        api.get('/ganapathi/drafts', { params: { status: tab, limit: 100 } }),
      ]);
      setState(s.data);
      setDrafts(d.data || []);
    } catch (e) {
      toast({ title: 'Failed to load', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);

  const cycleMode = async () => {
    const next = state?.mode === 'shadow' ? 'off' : (state?.mode === 'off' ? 'live' : 'shadow');
    if (!window.confirm(`Change Ganapathi mode to "${next}"?\n\n• off = disabled\n• shadow = drafts owner-only (trial)\n• live = drafts posted after approval, visible to team`)) return;
    try {
      await api.post('/ganapathi/state', { mode: next });
      toast({ title: `Mode → ${next}` });
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
  };

  const approve = async (d, text) => {
    setBusyId(d.id);
    try {
      const body = text && text !== d.draft_text ? { edited_text: text } : {};
      await api.post(`/ganapathi/drafts/${d.id}/approve`, body);
      toast({ title: '✅ Posted as Ganapathi in the session' });
      setEditingId(null);
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  const reject = async (d) => {
    const reason = window.prompt('Why reject? (optional — Ganapathi will learn from your reason)') || '';
    setBusyId(d.id);
    try {
      await api.post(`/ganapathi/drafts/${d.id}/reject`, { reason });
      toast({ title: 'Rejected' });
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  const replyAsSalesman = async (d) => {
    if (!salesmanReplyText.trim()) { toast({ title: 'Type a reply first' }); return; }
    setBusyId(d.id);
    try {
      await api.post(`/ganapathi/drafts/${d.id}/reply-as-salesman`, { text: salesmanReplyText });
      toast({ title: `Posted as ${d.salesman_name}` });
      setSalesmanReplyId(null);
      setSalesmanReplyText('');
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 shadow-md sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-emerald-700 p-2" data-testid="gp-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate flex items-center gap-2">
              <Sparkles className="w-5 h-5" /> Ganapathi Review
            </h1>
            <p className="text-xs text-emerald-100">
              {state?.mode ? `Mode: ${state.mode.toUpperCase()}` : '—'}
              {state?.active_directives != null && ` · ${state.active_directives} directives`}
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={load} className="text-white hover:bg-emerald-700" data-testid="gp-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-3 space-y-3">
        {/* Mode + settings row */}
        <div className="bg-white rounded-2xl shadow-sm p-3 flex items-center gap-2">
          <Badge className={
            state?.mode === 'live'   ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
            state?.mode === 'shadow' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                                       'bg-gray-100 text-gray-600 border-gray-300'
          }>
            {(state?.mode || 'off').toUpperCase()}
          </Badge>
          <Button size="sm" variant="outline" onClick={cycleMode} data-testid="gp-mode-cycle">
            Change mode
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => navigate('/ganapathi/settings')} data-testid="gp-settings-btn">
            Settings
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/ganapathi/chat')} data-testid="gp-chat-btn">
            <MessageCircle className="w-4 h-4 mr-1" /> Chat
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 bg-white rounded-2xl shadow-sm p-1">
          {['pending', 'approved', 'edit_approved', 'rejected', 'handled_by_owner_reply'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 text-xs font-medium rounded-xl py-2 transition ${
                tab === t ? 'bg-[#075E54] text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}
              data-testid={`gp-tab-${t}`}
            >
              {t === 'pending' && 'Pending'}
              {t === 'approved' && 'Approved'}
              {t === 'edit_approved' && 'Edited'}
              {t === 'rejected' && 'Rejected'}
              {t === 'handled_by_owner_reply' && 'Replied-as-SM'}
            </button>
          ))}
        </div>

        {loading && (
          <div className="text-center text-gray-500 py-8">Loading…</div>
        )}

        {!loading && drafts.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
            <Sparkles className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No {tab} drafts.</p>
            {tab === 'pending' && (
              <p className="text-xs text-gray-400 mt-2">
                Ganapathi will draft here when salesmen post in lead sessions{state?.mode === 'off' && ' — currently OFF, switch to SHADOW to enable'}.
              </p>
            )}
          </div>
        )}

        {drafts.map(d => (
          <div key={d.id} className="bg-white rounded-2xl shadow-sm p-4 space-y-3" data-testid={`gp-draft-${d.id}`}>
            {/* Meta */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <UserRound className="w-4 h-4 text-gray-400" />
                  <p className="text-sm font-semibold text-gray-800 truncate">{d.salesman_name || '—'}</p>
                  <span className="text-xs text-gray-400">•</span>
                  <p className="text-xs text-gray-600 truncate">{d.client_name || '—'}</p>
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {d.session_number && <span className="text-[10px] font-mono bg-gray-100 rounded px-1.5 py-0.5 text-gray-600">{d.session_number}</span>}
                  <span className="text-[10px] text-gray-400">{fmtTime(d.created_at)}</span>
                  {d.trigger_reason && (
                    <span className="text-[10px] bg-amber-50 text-amber-700 rounded px-1.5 py-0.5">
                      {TRIGGER_LABEL[d.trigger_reason] || d.trigger_reason}
                    </span>
                  )}
                </div>
              </div>
              <Button
                size="sm" variant="ghost"
                onClick={() => navigate(`/lead-sessions/${d.session_id}`)}
                data-testid={`gp-open-session-${d.id}`}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>

            {/* Draft text OR edit box */}
            {editingId === d.id ? (
              <textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                rows={5}
                className="w-full border border-emerald-300 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid={`gp-edit-textarea-${d.id}`}
              />
            ) : (
              <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-3">
                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {d.draft_text}
                </p>
                {d.final_text && d.final_text !== d.draft_text && (
                  <div className="mt-2 pt-2 border-t border-emerald-100">
                    <p className="text-[10px] text-emerald-700 font-semibold uppercase tracking-wide mb-1">Posted as (edited):</p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{d.final_text}</p>
                  </div>
                )}
                {d.rejection_reason && (
                  <div className="mt-2 pt-2 border-t border-red-100">
                    <p className="text-[10px] text-red-700 font-semibold uppercase tracking-wide mb-1">Rejection reason (saved as directive):</p>
                    <p className="text-xs text-red-700">{d.rejection_reason}</p>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            {d.status === 'pending' ? (
              <>
                {editingId === d.id ? (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditingId(null); }} data-testid={`gp-cancel-edit-${d.id}`}>
                      Cancel
                    </Button>
                    <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => approve(d, editText)} disabled={busyId === d.id} data-testid={`gp-save-edit-${d.id}`}>
                      <Check className="w-4 h-4 mr-1" /> Save & Post
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" className="flex-1 min-w-[100px] bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => approve(d)} disabled={busyId === d.id} data-testid={`gp-approve-${d.id}`}>
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 min-w-[100px]"
                      onClick={() => { setEditingId(d.id); setEditText(d.draft_text || ''); }} data-testid={`gp-edit-${d.id}`}>
                      <Edit3 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 min-w-[100px] text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => reject(d)} disabled={busyId === d.id} data-testid={`gp-reject-${d.id}`}>
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 min-w-[140px]"
                      onClick={() => { setSalesmanReplyId(salesmanReplyId === d.id ? null : d.id); setSalesmanReplyText(''); }}
                      data-testid={`gp-reply-as-sm-${d.id}`}>
                      <UserRound className="w-4 h-4 mr-1" /> Reply as {d.salesman_name?.split(' ')[0] || 'SM'}
                    </Button>
                  </div>
                )}

                {salesmanReplyId === d.id && (
                  <div className="border border-blue-200 rounded-xl p-2 bg-blue-50">
                    <p className="text-[10px] text-blue-700 mb-1 uppercase tracking-wide font-semibold">
                      Post as {d.salesman_name} (session-visible)
                    </p>
                    <textarea
                      value={salesmanReplyText}
                      onChange={e => setSalesmanReplyText(e.target.value)}
                      rows={3}
                      placeholder={`Type as ${d.salesman_name}…`}
                      className="w-full border border-blue-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      data-testid={`gp-sm-reply-text-${d.id}`}
                    />
                    <Button size="sm" className="mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => replyAsSalesman(d)} disabled={busyId === d.id} data-testid={`gp-sm-reply-submit-${d.id}`}>
                      <Send className="w-4 h-4 mr-1" /> Post
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-gray-400 flex items-center gap-2">
                {d.status === 'approved' && <span className="text-emerald-600">✓ Approved & posted</span>}
                {d.status === 'edit_approved' && <span className="text-emerald-600">✓ Edited & posted</span>}
                {d.status === 'rejected' && <span className="text-red-600">✗ Rejected</span>}
                {d.status === 'handled_by_owner_reply' && <span className="text-blue-600">↩ Posted as salesman</span>}
                {d.reviewed_by_name && <span>· by {d.reviewed_by_name}</span>}
                {d.reviewed_at && <span>· {fmtTime(d.reviewed_at)}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
